import { useEffect, useState, createContext, useContext, useRef } from 'react'
import { useQuery } from '@redwoodjs/web'
import type {
  PartCacheQuery,
  PartCacheQueryVariables
} from 'types/graphql'
import Sentry from './sentry'
import { simpleHash } from './util'
import { useDbContextMaybe, type DbApi } from './indexedDb'

import 'src/lib/queries'
import invariant from 'tiny-invariant'

// Context definition
type PartsCacheContextType = {
  cache: PartCacheQuery['partProtosCache'] | null
  loading: boolean
  refresh: (input?: RefreshInput) => void
}

type InMemoryCache = {
  data?: PartCacheQuery['partProtosCache']

  // used for tracking initial loading of the cache
  loadedFromLocal: boolean
  loadedFromRemote: boolean
}

export const PartsCacheContext = createContext<PartsCacheContextType | undefined>(undefined)

export function PartsCacheProvider({ children, orgId }: { children: React.ReactNode, orgId: string }) {
  const [cache, setCache] = useState<InMemoryCache>({ loadedFromLocal: false, loadedFromRemote: false })
  const indexedDb = useDbContextMaybe()
  const indexedDbReady = indexedDb && !indexedDb.isLoading

  const { refetch, loading } = useQuery<PartCacheQuery, PartCacheQueryVariables>(PART_PROTOS_CACHE_QUERY, {
    skip: true,
  })

  const fetchAndLoadCache = async () => {
    // query is already running
    if (loading || !indexedDbReady) return

    invariant(indexedDb)
    const { data } = await refetch({ ts: dateTimeToTs(cache.data?.lastUpdate) })
    if (data?.partProtosCache?.protos) {
      const updatedCache = data.partProtosCache
      setCache({
        ...cache,
        loadedFromRemote: true,
        data: updatedCache
      })
      await setCacheInIndexedDb(indexedDb.api, orgId, updatedCache)
    }
  }

  // Load cache from local store
  useEffect(() => {
    const loadLocalCache = async () => {
      try {
        invariant(indexedDb)
        // DB context guarantees initialization or error handling
        const data = await getCacheFromIndexedDb(indexedDb.api, orgId)
        setCache({
          ...cache,
          data,
          loadedFromLocal: true
        })
      } catch (error) {
        Sentry.captureException(error)
        console.error('Failed to load cache from IndexedDB:', error)
      }
    }

    if (indexedDbReady && !cache.loadedFromLocal) {
      loadLocalCache()
    }
  }, [orgId, indexedDbReady, cache])

  // Load cache from remote
  useEffect(() => {
    const loadRemoteCache = async () => {
      try {
        await fetchAndLoadCache()
      } catch (error) {
        Sentry.captureException(error)
        console.error('Failed to load protos cache from remote:', error)
      }
    }
    if (cache.loadedFromLocal && !cache.loadedFromRemote) {
      loadRemoteCache()
    }
  }, [cache])

  const refresh = async ({ debounce } : RefreshInput = {}) => {
    invariant(indexedDb)
    if (!cache.loadedFromLocal || !cache.loadedFromRemote) return

    try {
      const lastQueryTimestamp = await indexedDb.api.keyValueStore.get<string>(CACHE_TIMESTAMP_NAME)
      const now = Date.now()

      if (debounce && lastQueryTimestamp && now - parseInt(lastQueryTimestamp, 10) < 60 * 1000) {
        return
      }

      await fetchAndLoadCache()
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  return (
    <PartsCacheContext.Provider value={{ cache: cache.data, refresh, loading: !cache.loadedFromLocal || !cache.loadedFromRemote }}>
      {children}
    </PartsCacheContext.Provider>
  )
}

function usePartsCacheContext() {
  const context = useContext(PartsCacheContext)
  if (context === undefined) {
    throw new Error('usePartsCacheContext must be used within a PartsCacheProvider')
  }
  return context
}

export const PART_PROTOS_CACHE_QUERY = gql`
  query PartCacheQuery($ts: String!) {
    partProtosCache(ts: $ts) {
      lastUpdate
      orphanParts {
        tree {
          ...PartTreeNodeFragment
        }
      }
      protos {
        id
        inChangeOrders {
          number
          name
          state
        }
        category {
          id
          name
        }
        updatedAt
        partNumber
        protoUpdatedBy {
          name
        }
        currentVersionString
        currentVersion {
          ...PartTreeNodePartFragment
        }
        owner {
          id
          name
        }
      }
    }
  }
`

const CACHE_NAME = 'partProtosCache'
const CACHE_TIMESTAMP_NAME = 'partProtosCacheTimestamp'
const CACHE_KEY_NAME = 'partProtosCacheKey'

// always default in dev env
const SOURCE_VERSION = process.env.SOURCE_VERSION || 'default'

const queryHash = simpleHash(JSON.stringify(PART_PROTOS_CACHE_QUERY, (key, value) =>
  typeof value === 'function' ? value.toString() : value
))

const getCacheKeyValue = (orgId: string) => {
  return `${SOURCE_VERSION}::${queryHash}::${orgId}`
}

const getCacheFromIndexedDb = async (api: DbApi, orgId: string): Promise<PartCacheQuery['partProtosCache'] | null> => {
  try {
    const cachedData = await api.keyValueStore.get<PartCacheQuery['partProtosCache']>(CACHE_NAME)
    const lastCacheKey = await api.keyValueStore.get<string>(CACHE_KEY_NAME)
    const nextCacheKey = getCacheKeyValue(orgId)

    // delete the cache due to change in org, query or bundle version
    if (nextCacheKey !== lastCacheKey) {
      console.log('Clearing protos cache')
      await api.keyValueStore.remove(CACHE_NAME)
      await api.keyValueStore.remove(CACHE_TIMESTAMP_NAME)
      await api.keyValueStore.remove(CACHE_KEY_NAME)
      return null
    }

    return cachedData
  } catch (error) {
    Sentry.captureException(error)
    console.error('Error getting cache from IndexedDB:', error)
    return null
  }
}

const setCacheInIndexedDb = async (api: DbApi, orgId: string, cache: PartCacheQuery['partProtosCache']) => {
  try {
    await api.keyValueStore.set(CACHE_NAME, cache)
    await api.keyValueStore.set(CACHE_TIMESTAMP_NAME, Date.now().toString())
    await api.keyValueStore.set(CACHE_KEY_NAME, getCacheKeyValue(orgId))
  } catch (error) {
    Sentry.captureException(error)
    console.error('Error setting cache in IndexedDB:', error)
  }
}

type RefreshInput = {
  debounce?: boolean
}

const dateTimeToTs = (input?: string | null) => {
  if (!input) return '0'
  return String((new Date(input)).getTime())
}

export type PartsCacheData = Required<PartCacheQuery['partProtosCache']>

export const usePartsCache = (): {
  data: PartsCacheData
  loading: boolean
  refresh: (input?: RefreshInput) => void
} => {
  const { cache, loading, refresh } = usePartsCacheContext()

  return {
    data: cache as PartsCacheData,
    loading,
    refresh,
  }
}
